import {
  buildQueryString,
  request,
  requestBlobWithHeaders,
  requestWithHeaders,
  serializeCsvQuery,
} from "../client"
import type { ContactOrder } from "../contact-orders"
import type { DueLetterDeliveryStep } from "../letters"
import type { KehrblattPlanningSettings } from "../planning"

export type WorkOrderListResponse = {
  items: WorkOrder[]
  total: number
}

export type WorkOrderOpenTarget = {
  id: string
  order_number?: string | null
  order_types?: WorkOrderOrderTypeItem[] | null
  property_id: string
  property_unit_id?: string | null
  property_unit_label?: string | null
}

export type WorkOrderProjectionBundle = {
  properties: Property[]
  customers: Customer[]
  property_customers: PropertyCustomer[]
  contact_points: ContactPoint[]
  schedule_events: ScheduleEvent[]
  contact_orders: ContactOrder[]
  schedule_event_communication_presentations?: Record<
    string,
    ScheduleEventCommunicationPresentation
  >
  due_letter_delivery_steps_by_work_order_id?: Record<string, DueLetterDeliveryStep[]>
  notification_counts?: WorkOrderNotificationCount[]
  order_types: OrderType[]
}

export type ScheduleEventCommunicationPresentation = {
  schedule_event_id: string
  state:
    | "RESERVED"
    | "ANNOUNCEMENT_SENT"
    | "LETTER_SENT"
    | "LETTER_PENDING"
    | "LETTER_QUEUED"
    | "ANNOUNCEMENT_UNKNOWN"
    | "ANNOUNCEMENT_SKIPPED"
    | "ANNOUNCEMENT_RUNNING"
    | "ANNOUNCEMENT_FAILED"
    | "ANNOUNCEMENT_UNSECURED"
    | "WHATSAPP_READ"
    | "LETTER_DELIVERED"
    | "SECURELY_ANNOUNCED"
    | "CONFIRMED"
    | "COMPLETED"
    | "CANCELED"
    | "NOSHOW"
  label: string
  detail?: string | null
  evidence_kind?: "communication_sent" | "whatsapp_read" | "letter_delivered" | "letter_sent" | null
  evidence_channel?: string | null
  evidence_at?: string | null
  announcement_label?: string | null
  next_action?: "none" | "automatic" | "manual_letter" | "plan" | "review"
  next_action_label?: string | null
  next_action_at?: string | null
}

export type WorkOrderTableProjection = WorkOrderListResponse & WorkOrderProjectionBundle

export type WorkOrderNotificationCount = {
  work_order_id: string
  case_id?: string | null
  open_count: number
  open_items?: WorkOrderNotificationPreview[]
}

export type WorkOrderNotificationPreview = {
  id: string
  title: string
  event_type?: string | null
}

export type WorkOrderOverviewBootstrapProjection = {
  order_types: OrderType[]
  planning_settings?: KehrblattPlanningSettings | null
  next_order_number?: string | null
}

export type WorkOrderDetailProjection = WorkOrderProjectionBundle & {
  work_order: WorkOrder
}

export type WorkOrderPlanningContactOrderSummary = {
  id: string
  organization_id: string
  case_id: string
  work_order_id: string
  schedule_event_id: string | null
  reason: string
  status: string
  closed_reason: string | null
  closed_reason_details: string | null
  proposed_start_at: string | null
  proposed_end_at: string | null
  assigned_employee_ids: string[]
  created_at: string | null
  updated_at: string | null
}

export type WorkOrderPlanningProjection = {
  work_order: WorkOrder
  properties: Property[]
  customers: Customer[]
  contact_points: ContactPoint[]
  schedule_events: ScheduleEvent[]
  contact_orders: WorkOrderPlanningContactOrderSummary[]
  schedule_event_communication_presentations: Record<string, ScheduleEventCommunicationPresentation>
}

export type WorkOrderBatchActionIssue = {
  work_order_id: string
  reason_code: string
  message: string
}

export type WorkOrderBatchActionResponse = {
  action: "complete" | "archive"
  requested_count: number
  unique_count: number
  processed_count: number
  skipped_count: number
  failed_count: number
  processed_work_order_ids: string[]
  skipped: WorkOrderBatchActionIssue[]
  failed: WorkOrderBatchActionIssue[]
}

export type WorkOrderTourSheetRequest = {
  organization_id: string
  archive_scope?: ArchiveScope
  search_query?: string | null
  order_type_ids?: string[]
  appointment_date_from?: string | null
  appointment_date_to?: string | null
  employee_ids?: string[]
  work_order_state?: string[]
  action_required_state?: string | null
  due_date_from?: string | null
  due_date_to?: string | null
}

export type WorkOrderTourSheetDownload = {
  blob: Blob
  filename: string
}

export type WorkOrderCommunicationProofDownload = {
  blob: Blob
  filename: string
}

const filenameFromContentDisposition = (value: string | null, fallback: string) => {
  if (!value) return fallback

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i)

  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]).trim() || fallback
    } catch {
      return fallback
    }
  }

  const asciiMatch = value.match(/filename="?([^";]+)"?/i)

  return asciiMatch?.[1]?.trim() || fallback
}

const workOrderQueryParams = (query?: WorkOrderQuery) => ({
  required_access: query?.requiredAccess,
  organization_id: query?.organizationId,
  property_id: query?.propertyId,
  property_unit_id: query?.propertyUnitId,
  property_ids: serializeCsvQuery(query?.propertyIds),
  property_unit_ids: serializeCsvQuery(query?.propertyUnitIds),
  order_number: query?.orderNumber,
  order_type_ids: serializeCsvQuery(query?.orderTypeIds),
  address_query: query?.addressQuery,
  search_query: query?.searchQuery,
  status: query?.status,
  view_status: query?.viewStatus,
  employee_ids: serializeCsvQuery(query?.employeeIds),
  appointment_status: query?.appointmentStatus,
  appointment_available: query?.appointmentAvailable,
  appointment_employee_id: query?.appointmentEmployeeId,
  work_order_state: serializeCsvQuery(query?.workOrderStates),
  action_required_state: query?.actionRequiredState,
  appointment_date_from: query?.appointmentDateFrom,
  appointment_date_to: query?.appointmentDateTo,
  contact_channel: serializeCsvQuery(query?.contactChannels),
  due_date_from: query?.dueDateFrom,
  due_date_to: query?.dueDateTo,
  work_order_ids: serializeCsvQuery(query?.workOrderIds),
  sort_by: query?.sortBy,
  sort_dir: query?.sortDir,
  offset: query?.offset,
  limit: query?.limit,
  archive_scope: query?.archiveScope,
})

export const fetchWorkOrders = (query?: WorkOrderQuery) =>
  request<WorkOrder[]>(`/work-orders${buildQueryString(workOrderQueryParams(query))}`)

export const fetchOpenWorkOrderTargets = (
  query?: Pick<WorkOrderQuery, "organizationId" | "propertyIds" | "requiredAccess">,
) =>
  request<WorkOrderOpenTarget[]>(
    `/work-orders${buildQueryString({
      projection: "open_targets",
      organization_id: query?.organizationId,
      property_ids: serializeCsvQuery(query?.propertyIds),
      required_access: query?.requiredAccess,
      status: "OPEN",
    })}`,
  )

export const fetchWorkOrdersWithTotal = async (
  query?: WorkOrderQuery,
): Promise<WorkOrderListResponse> => {
  const { data, headers } = await requestWithHeaders<WorkOrder[]>(
    `/work-orders${buildQueryString(workOrderQueryParams(query))}`,
  )
  const rawTotal = headers.get("x-total-count")
  const total = rawTotal ? Number(rawTotal) : data.length

  return { items: data, total: Number.isFinite(total) ? total : data.length }
}

export const fetchWorkOrderTable = (query?: WorkOrderQuery) =>
  request<WorkOrderTableProjection>(
    `/work-orders${buildQueryString({
      projection: "table",
      ...workOrderQueryParams(query),
    })}`,
  )

export const fetchWorkOrderTourSheetPdf = async (
  payload: WorkOrderTourSheetRequest,
): Promise<WorkOrderTourSheetDownload> => {
  const { data, headers } = await requestBlobWithHeaders("/work-orders/tour-sheet/pdf", {
    method: "POST",
    body: JSON.stringify(payload),
  })

  return {
    blob: data,
    filename: filenameFromContentDisposition(
      headers.get("content-disposition"),
      "tourenzettel-gefilterte-auftraege.pdf",
    ),
  }
}

export const fetchWorkOrderCommunicationProofPdf = async (
  workOrderId: string,
): Promise<WorkOrderCommunicationProofDownload> => {
  const { data, headers } = await requestBlobWithHeaders(
    `/work-orders/${workOrderId}/communication-proof/pdf`,
  )

  return {
    blob: data,
    filename: filenameFromContentDisposition(
      headers.get("content-disposition"),
      `nachweis-auftrag-${workOrderId}.pdf`,
    ),
  }
}

export const fetchWorkOrderOverviewBootstrap = (organizationId?: string) =>
  request<WorkOrderOverviewBootstrapProjection>(
    `/work-orders${buildQueryString({
      projection: "overview_bootstrap",
      organization_id: organizationId,
    })}`,
  )

export const fetchNextWorkOrderNumber = (organizationId?: string) =>
  request<WorkOrderNextNumberResponse>(
    `/work-orders/next-order-number${buildQueryString({
      organization_id: organizationId,
    })}`,
  )

export const createWorkOrder = (payload: WorkOrderCreatePayload) =>
  request<WorkOrder>("/work-orders", {
    method: "POST",
    body: JSON.stringify(payload),
  })

export const createWorkOrdersBatch = (payload: WorkOrderCreatePayload[], requestId?: string) =>
  request<WorkOrder[]>("/work-orders/batch", {
    method: "POST",
    headers: requestId ? { "Idempotency-Key": requestId } : undefined,
    body: JSON.stringify(payload),
  })

export const fetchWorkOrder = (workOrderId: string) =>
  request<WorkOrder>(`/work-orders/${workOrderId}`)

export const fetchWorkOrderDetailProjection = (workOrderId: string) =>
  request<WorkOrderDetailProjection>(
    `/work-orders/${workOrderId}${buildQueryString({ projection: "detail" })}`,
  )

export const fetchWorkOrderPlanningProjection = (workOrderId: string) =>
  request<WorkOrderPlanningProjection>(
    `/work-orders/${workOrderId}${buildQueryString({ projection: "planning" })}`,
  )

export const updateWorkOrder = (workOrderId: string, payload: WorkOrderUpdatePayload) =>
  request<WorkOrder>(`/work-orders/${workOrderId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  })

export const reopenWorkOrder = (workOrderId: string) =>
  request<WorkOrder>(`/work-orders/${workOrderId}/reopen`, {
    method: "POST",
  })

export const completeWorkOrdersBatch = (workOrderIds: string[]) =>
  request<WorkOrderBatchActionResponse>("/work-orders/complete-batch", {
    method: "POST",
    body: JSON.stringify({ work_order_ids: workOrderIds }),
  })

export const archiveWorkOrdersBatch = (
  workOrderIds: string[],
  archiveReason: ArchiveReason = "USER_REQUEST",
) =>
  request<WorkOrderBatchActionResponse>("/work-orders/archive-batch", {
    method: "POST",
    body: JSON.stringify({ work_order_ids: workOrderIds, archive_reason: archiveReason }),
  })
